import {
  Accordion,
  Autocomplete,
  Avatar,
  Box,
  Card,
  CardContent,
  Drawer,
  FormControl,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Select,
  StepLabel,
  Stepper,
  TextField
} from "@mui/material"
import { styled } from "@mui/material/styles"
import cyan from "../../css/theme/colors/cyan"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

export const StyledListItemText = styled(ListItemText)(({ theme }) => ({
  "& .MuiListItemText-primary": {
    color: theme.palette.foreground.default,
    fontWeight: 600
  },
  "& .MuiListItemText-secondary": {
    fontSize: "16px"
  }
}))

export const StyledAutocomplete = styled(Autocomplete)(({ theme }) => ({
  display: "flex",
  width: "100%"
}))

export const StyledBox = styled(Box)({
  display: "flex",
  width: "100%"
})

export const StyledShadowBox = styled(Box)({
  boxShadow: "0px 0px 9px 0px rgba(0, 0, 0, 0.1)"
})

export const StyledLargeDrawer = styled(Drawer)(({ theme }) => ({
  flexShrink: 1,
  "& > .MuiPaper-root": {
    backgroundColor: theme.palette.foreground.inverted,
    width: "450px",
    top: 48
  }
}))

export const StyledListItemIcon = styled(ListItemIcon)({
  paddingRight: "12px",
  paddingTop: "4px"
})

export const StyledCard = styled(Card)({
  display: "flex"
})

export const StyledCardContent = styled(CardContent)({
  display: "flex",
  alignItems: "center",
  padding: "24px"
})

export const StyledAvatar = styled(Avatar)(({ theme }) => ({
  width: "60px",
  height: "60px",
  background: cyan[100],
  color: theme.palette.primary.dark,
  marginRight: "16px"
}))

export const StyledList = styled(List)(({ theme }) => ({
  "& .MuiListItem-root:hover": {
    background: "transparent",
    color: theme.palette.foreground.inverted
  },
  "& .MuiButtonBase-root": {
    color: theme.palette.foreground.inverted
  }
}))

export const StyledAccordion = styled(Accordion)(({ theme }) => ({
  background: "none",
  color: theme.palette.foreground.inverted,
  borderBottom: 0,
  width: "100%",
  "& .MuiAccordionSummary-root": {
    padding: 0
  }
}))

export const StyledStepper = styled(Stepper)(({ theme }) => ({
  background: "transparent",
  marginLeft: "auto",
  marginRight: "auto",
  marginTop: 40,
  marginBottom: 40,
  display: "flex",
  justifyContent: "center",
  "& .MuiStepLabel-label.Mui-disabled": {
    color: theme.palette.grey[400],
    opacity: 1
  }
}))

export const StyledStepLabel = styled(StepLabel)(({ theme }) => ({
  marginTop: "-2px",
  fontSize: "2rem",
  cursor: "pointer",
  padding: 0,
  "& .MuiStepLabel-label": {
    fontSize: "16px",
    paddingBottom: "4px"
  },
  "& .MuiStepLabel-label.Mui-active": {
    borderBottom: "2px solid",
    color: theme.palette.primary[800]
  }
}))

export const StyledDrawerList = styled(List)(({ theme }) => ({
  width: "100%",
  backgroundColor: theme.palette.background.paper,
  padding: "1rem 2rem 2rem 2rem"
}))

export const sharedListItemStyle = {
  borderRadius: "0 !important",
  padding: "0",
  alignItems: "flex-start !important",
  display: "flex",
  flexDirection: "column"
}
export const StyledListItem = styled(ListItem)(sharedListItemStyle)

export const StyledTextField = styled(TextField)({
  "& .MuiInputBase-input": {
    padding: "16px 12px"
  }
})

export const StyledWhiteBox = styled(Box)({
  background: "white"
})

export const StyledCustomListItemIcon = styled(ListItemIcon)(({ theme, size }) => ({
  borderRadius: "100vh",
  width: size,
  height: size,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  marginRight: "12px"
}))

export const StyledGreySeparator = styled(Box)(({ theme }) => ({
  borderTop: "1px solid",
  borderColor: theme.palette.grey[300]
}))

export const StyledFormControl = styled(FormControl)(({ theme }) => ({
  margin: theme.spacing(1),
  minWidth: 120
}))

export const StyledFontAwesomeIconForButton = styled(
  ({ iconSize, ...props }) => <FontAwesomeIcon {...props} />,
  { shouldForwardProp: (prop) => prop !== "iconSize" }
)(({ iconSize }) => {
  let fontSize
  switch (iconSize) {
    case "xs":
      fontSize = "11px"
      break
    case "sm":
      fontSize = "14px"
      break
    case "md":
      fontSize = "16px"
      break
    case "lg":
      fontSize = "18px"
      break
    default:
      fontSize = "14px"
  }
  return {
    fontSize,
    verticalAlign: "middle"
  }
})

export const StyledTableFormControl = styled(FormControl)(({ theme }) => ({
  minWidth: 200,
  "& .MuiFormLabel-root": {
    color: theme.palette.default[900],
    opacity: 1
  },
  "& .MuiSvgIcon-root": {
    color: theme.palette.default[600]
  }
}))

export const StyledTableSearchTextfield = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    minWidth: 300,
    backgroundColor: theme.palette.default[100],
    color: theme.palette.default[600],
    borderRadius: 8,
    "& fieldset": {
      border: "none"
    },
    "&:hover fieldset": {
      border: `1px solid ${theme.palette.secondary[600]}`
    },
    "&.Mui-focused fieldset": {
      border: `2px solid ${theme.palette.secondary[600]}`
    },
    "&.Mui-focused:hover fieldset": {
      border: `2px solid ${theme.palette.secondary[600]}`
    }
  }
}))

export const StyledTableDefaultTextfield = styled(TextField)(({ theme }) => ({
  width: "100%",
  "& .MuiOutlinedInput-root": {
    width: "100%",
    borderRadius: 8,
    "& fieldset": {
      borderRadius: 8
    }
  }
}))

export const StyledTableColumnSelect = styled(Select)(({ theme }) => ({
  borderRadius: 8,
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: theme.palette.default[300]
    }
  }
}))

export const StyledRowGapBox = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(2),
  alignItems: "center"
}))

export const StyledColumnGapBox = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(2),
  alignItems: "center"
}))

export const StyledSectionCard = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  border: "1px solid",
  borderColor: theme.palette.grey[300],
  borderRadius: theme.spacing(3),
  padding: theme.spacing(3)
}))
